import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  inject,
  input,
  signal,
  untracked,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslocoPipe } from '@jsverse/transloco';
import {
  PaperButtonComponent,
  PaperErrorBannerComponent,
  PaperInlineMessageComponent,
  PaperInputComponent,
  PaperSelectComponent,
  PaperSkeletonComponent,
  PaperTagComponent,
} from '@ricoh-paper/angular';
import { BookMeetingService } from '../../../../../core/services/book-meeting.service';
import {
  DraftProblem,
  MeetingVisitorsService,
} from '../../../../../core/services/meeting-visitors.service';
import { HostVisitsService } from '../../../../../core/services/host-visits.service';
import { AnalyticsService } from '../../../../../core/services/analytics.service';
import { ANALYTICS_EVENTS } from '../../../../../core/analytics/events';
import { ApiError, isErr } from '../../../../../core/models/api-result';
import type {
  HostContactDto,
  HostVisitRow,
} from '../../../../../core/models/host-visit.model';
import {
  ARRIVAL_INTENT,
  PRE_ARRIVAL_INTENT,
  arrivalKey,
  preArrivalKey,
} from '../../../../visitors/visitor-format';

const PROBLEM_KEY: Record<DraftProblem, string> = {
  name: 'visitors.meeting.problem_name',
  email: 'visitors.meeting.problem_email',
  duplicate: 'visitors.meeting.problem_duplicate',
};

/**
 * RETIRING PANEL — DO NOT EXTEND. This lives in the Book a Meeting panel,
 * which is being decommissioned; it stays only for the transitional fallback.
 * Visitors on meetings are built on `MeetingVisitorsService` and rendered by
 * `ImMeetingVisitorsComponent` (features/visitors/) in the /schedule event
 * panel and the Calendar (Beta) side panel. Fix bugs here; add nothing.
 */
/**
 * Visitors on a room booking — the "Add visitors" section of the Book a
 * Meeting panel.
 *
 * CREATE mode: the host adds visitors (their own saved contacts or typed in)
 * and picks why they are coming. Nothing is sent here — once the room is
 * booked, `BookMeetingService.submitBooking` raises one visit per visitor,
 * linked to the booking (`MeetingVisitorsService`).
 *
 * VIEW mode: the visitors the viewer invited to this meeting, with where they
 * are — `myVisits(bookingId)`, which the API bounds to the viewer's own
 * visits, so an attendee who did not invite anybody sees an empty list.
 *
 * The panel mounts this only when the `visitors` flag is on, and in create
 * mode only for a viewer who can raise a visit (`visit:create`).
 */
@Component({
  selector: 'im-bmp-visitors',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    TranslocoPipe,
    PaperButtonComponent,
    PaperErrorBannerComponent,
    PaperInlineMessageComponent,
    PaperInputComponent,
    PaperSelectComponent,
    PaperSkeletonComponent,
    PaperTagComponent,
  ],
  host: { class: 'ph-no-capture' },
  template: `
    @if (viewMode()) {
      @if (linkedLoading()) {
        <paper-skeleton variant="line" height="40px" width="100%" />
      } @else {
        <paper-error-banner [error]="linkedError()" [compact]="true" (retry)="loadLinked()" />
        @if (!linkedError()) {
          @if (linked().length === 0) {
            <p class="bv__hint">{{ 'visitors.meeting.none_linked' | transloco }}</p>
          } @else {
            <ul class="bv__list">
              @for (row of linked(); track row.id) {
                <li class="bv__row">
                  <div class="bv__who">
                    <span class="bv__name ph-mask">{{ row.visitorName }}</span>
                    @if (row.company) {
                      <span class="bv__meta ph-mask">{{ row.company }}</span>
                    }
                  </div>
                  <div class="bv__tags">
                    @if (arrivalKey(row.arrival); as key) {
                      <paper-tag size="xs" [intent]="arrivalIntent[row.arrival]">{{ key | transloco }}</paper-tag>
                    }
                    @if (preArrivalKey(row.preArrival); as key) {
                      <paper-tag size="xs" [intent]="preIntent[row.preArrival]">{{ key | transloco }}</paper-tag>
                    }
                  </div>
                </li>
              }
            </ul>
          }
        }
      }
    } @else {
      <!-- ── Why they are coming ─────────────────────────── -->
      @if (!roomLocationId()) {
        <p class="bv__hint">{{ 'visitors.meeting.pick_room_first' | transloco }}</p>
      } @else {
        @if (visitors.reasonsLoading()) {
          <paper-skeleton variant="line" height="40px" width="100%" />
        } @else if (visitors.reasonsError(); as error) {
          <paper-inline-message intent="warning">
            {{ (error.code === 'FORBIDDEN' ? 'visitors.invite.reasons_forbidden' : 'visitors.invite.reasons_error') | transloco }}
          </paper-inline-message>
        } @else if (visitors.reasons().length === 0) {
          <paper-inline-message intent="warning">{{ 'visitors.invite.reasons_none' | transloco }}</paper-inline-message>
        } @else {
          <paper-select
            name="visitReason"
            [label]="'visitors.invite.why' | transloco"
            [options]="reasonOptions()"
            [placeholder]="'visitors.meeting.reason_placeholder' | transloco"
            [invalid]="visitors.hasDrafts() && !visitors.reasonId()"
            [ngModel]="visitors.reasonId() ?? ''"
            (ngModelChange)="visitors.setReason($event || null)"
            data-testid="bmp-visitors-reason"
          />
          @if (reasonNote(); as note) {
            <p class="bv__hint">{{ note }}</p>
          }
        }

        <!-- ── Who ──────────────────────────────────────── -->
        @if (visitors.drafts().length) {
          <ul class="bv__list" data-testid="bmp-visitors-drafts">
            @for (draft of visitors.drafts(); track draft.key) {
              <li class="bv__row">
                <div class="bv__who">
                  <span class="bv__name ph-mask">{{ draft.name }}</span>
                  <span class="bv__meta ph-mask">{{ draftMeta(draft.company, draft.email) }}</span>
                </div>
                <paper-button
                  intent="ghost"
                  size="sm"
                  icon="x"
                  [ariaLabel]="'visitors.meeting.remove' | transloco: { name: draft.name }"
                  (click)="visitors.removeDraft(draft.key)"
                />
              </li>
            }
          </ul>
        }

        @if (contacts().length) {
          <paper-input
            class="ph-mask"
            type="search"
            name="visitorContactQuery"
            prefix="search"
            [label]="'visitors.invite.search_contacts' | transloco"
            [ngModel]="contactQuery()"
            (ngModelChange)="contactQuery.set($event)"
          />
          @if (contactQuery().trim()) {
            <div class="bv__contacts" role="list">
              @for (contact of contactMatches(); track contact._id) {
                <div role="listitem">
                  <paper-button class="ph-mask" intent="ghost" size="sm" icon="user-plus" [block]="true" (click)="addContact(contact)">
                    {{ contactLabel(contact) }}
                  </paper-button>
                </div>
              } @empty {
                <p class="bv__hint">{{ 'visitors.invite.no_contacts_match' | transloco }}</p>
              }
            </div>
          }
        }

        <div class="bv__add">
          <paper-input
            class="ph-mask"
            name="visitorName"
            [label]="'visitors.invite.name' | transloco"
            [ngModel]="visitors.pending().name"
            (ngModelChange)="visitors.setPending('name', $event)"
            data-testid="bmp-visitors-name"
          />
          <paper-input
            class="ph-mask"
            type="email"
            name="visitorEmail"
            [label]="'visitors.invite.email' | transloco"
            [ngModel]="visitors.pending().email"
            (ngModelChange)="visitors.setPending('email', $event)"
          />
          <paper-input
            class="ph-mask"
            name="visitorCompany"
            [label]="'visitors.invite.company' | transloco"
            [ngModel]="visitors.pending().company"
            (ngModelChange)="visitors.setPending('company', $event)"
          />
          @if (visitors.hasPending() && !problem()) {
            <p class="bv__hint">{{ 'visitors.meeting.pending_hint' | transloco }}</p>
          }
          @if (problem(); as key) {
            <p class="bv__problem" role="alert">{{ key | transloco }}</p>
          }
          <paper-button
            intent="secondary"
            size="sm"
            icon="user-plus"
            [disabled]="!visitors.pending().name.trim()"
            data-testid="bmp-visitors-add"
            (click)="addTyped()"
          >
            {{ 'visitors.meeting.add' | transloco }}
          </paper-button>
        </div>

        <p class="bv__hint">{{ 'visitors.meeting.what_happens' | transloco }}</p>
      }
    }
  `,
  styles: `
    :host { display: flex; flex-direction: column; gap: 10px; }
    .bv__hint { margin: 0; font-size: 12px; color: var(--text-tertiary); }
    .bv__problem { margin: 0; font-size: 12px; color: var(--error-text); }
    .bv__list {
      list-style: none; margin: 0; padding: 0;
      display: flex; flex-direction: column; gap: 6px;
    }
    .bv__row {
      display: flex; align-items: center; justify-content: space-between; gap: 8px;
      min-height: 44px; padding: 6px 10px;
      background: var(--surface-1);
      border: 1px solid var(--border-low);
      border-radius: var(--radius-sm);
    }
    .bv__who { display: flex; flex-direction: column; min-width: 0; }
    .bv__name {
      font-size: 13px; font-weight: 600; color: var(--text);
      overflow-wrap: anywhere;
    }
    .bv__meta {
      font-size: 12px; color: var(--text-secondary);
      overflow-wrap: anywhere;
    }
    .bv__tags { display: flex; flex-wrap: wrap; gap: 4px; justify-content: flex-end; }
    .bv__contacts { display: flex; flex-direction: column; gap: 2px; }
    .bv__add { display: flex; flex-direction: column; gap: 8px; }
  `,
})
export class BmpVisitorsSectionComponent {
  readonly viewMode = input(false);

  protected readonly visitors = inject(MeetingVisitorsService);
  private readonly bmp = inject(BookMeetingService);
  private readonly host = inject(HostVisitsService);
  private readonly analytics = inject(AnalyticsService);

  protected readonly arrivalIntent = ARRIVAL_INTENT;
  protected readonly preIntent = PRE_ARRIVAL_INTENT;
  protected readonly arrivalKey = arrivalKey;
  protected readonly preArrivalKey = preArrivalKey;

  protected readonly problem = signal<string | null>(null);
  protected readonly contacts = signal<readonly HostContactDto[]>([]);
  protected readonly contactQuery = signal('');

  protected readonly linked = signal<readonly HostVisitRow[]>([]);
  protected readonly linkedLoading = signal(false);
  protected readonly linkedError = signal<ApiError | null>(null);

  private contactsLoaded = false;

  protected readonly roomLocationId = computed(
    () => this.bmp.primaryRoom()?.locationId ?? null,
  );
  protected readonly reasonOptions = computed(() =>
    this.visitors.reasons().map((r) => ({ value: r._id, label: r.name })),
  );
  protected readonly reasonNote = computed(
    () =>
      this.visitors.reasons().find((r) => r._id === this.visitors.reasonId())
        ?.consequenceNote ?? null,
  );
  protected readonly contactMatches = computed(() => {
    const q = this.contactQuery().trim().toLowerCase();
    if (!q) return [];
    return this.contacts()
      .filter((c) =>
        [c.firstName, c.lastName, c.company, c.email]
          .filter(Boolean)
          .some((part) => (part as string).toLowerCase().includes(q)),
      )
      .slice(0, 5);
  });

  constructor() {
    // Create mode: the room's site decides which reasons exist.
    effect(() => {
      const locationId = this.roomLocationId();
      const view = this.viewMode();
      untracked(() => {
        if (view) return;
        void this.visitors.loadReasons(locationId);
        void this.loadContacts();
      });
    });
    // View mode: who I invited to THIS booking.
    effect(() => {
      const bookingId = this.bmp.loadedBookingId();
      const view = this.viewMode();
      untracked(() => {
        if (view && bookingId) void this.loadLinked();
      });
    });
  }

  protected addTyped(): void {
    const problem = this.visitors.addPending();
    this.problem.set(problem ? PROBLEM_KEY[problem] : null);
    if (!problem) this.report(false);
  }

  protected addContact(contact: HostContactDto): void {
    const problem = this.visitors.addDraft({
      name: [contact.firstName, contact.lastName].filter(Boolean).join(' '),
      email: contact.email ?? '',
      company: contact.company ?? '',
      fromContact: true,
    });
    this.problem.set(problem ? PROBLEM_KEY[problem] : null);
    this.contactQuery.set('');
    if (!problem) this.report(true);
  }

  protected contactLabel(contact: HostContactDto): string {
    const name =
      [contact.firstName, contact.lastName].filter(Boolean).join(' ') ||
      contact.email ||
      '';
    return contact.company ? `${name} · ${contact.company}` : name;
  }

  protected draftMeta(company: string, email: string): string {
    return [company, email].filter(Boolean).join(' · ');
  }

  async loadLinked(): Promise<void> {
    const bookingId = this.bmp.loadedBookingId();
    if (!bookingId) return;
    this.linkedLoading.set(true);
    this.linkedError.set(null);
    const result = await this.host.visitsForBooking(bookingId);
    this.linkedLoading.set(false);
    if (isErr(result)) {
      this.linkedError.set(result.error);
      return;
    }
    this.linked.set(result.data);
  }

  private async loadContacts(): Promise<void> {
    if (this.contactsLoaded) return;
    this.contactsLoaded = true;
    const book = await this.host.addressBook();
    // A failed read only removes a shortcut; typing a visitor still works.
    if (!isErr(book)) this.contacts.set(book.data);
  }

  private report(fromContact: boolean): void {
    this.analytics.capture(ANALYTICS_EVENTS.VISITORS_MEETING_VISITOR_ADDED, {
      from_contact: fromContact,
      visitor_count: this.visitors.drafts().length,
      location_id: this.roomLocationId() ?? '',
    });
  }
}
